import { spacing } from "./spacing";

export const typographyBase = {
  fontFamily: {
    default:
      '"InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  },

  fontSize: {
    xs: 0.75,
    sm: 0.875,
    md: 1,
    lg: 1.25,
    xl: 1.5,
    xxl: 2,
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },

  lineHeight: {
    sm: 1.125,
    md: 1.5,
    lg: 1.75,
    xl: 2,
    xxl: 2.5,
  },

  letterSpacing: {
    default: -0.013,
    compact: -0.025,
  },
} as const;

// Type exports for TypeScript type safety
export type FontFamily = keyof typeof typographyBase.fontFamily;
export type FontSize = keyof typeof typographyBase.fontSize;
export type FontWeight = keyof typeof typographyBase.fontWeight;
export type LineHeight = keyof typeof typographyBase.lineHeight;
export type LetterSpacing = keyof typeof typographyBase.letterSpacing;

/**
 * Typographic Styles
 * Semantic styles composed from primitive tokens
 */
export const typographicStyles = {
  display: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.xxl,
    fontWeight: typographyBase.fontWeight.bold,
    letterSpacing: typographyBase.letterSpacing.compact,
    lineHeight: typographyBase.lineHeight.xxl,
  },

  titleLarge: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.xl,
    fontWeight: typographyBase.fontWeight.semiBold,
    letterSpacing: typographyBase.letterSpacing.compact,
    lineHeight: typographyBase.lineHeight.xl,
  },

  titleMedium: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.lg,
    fontWeight: typographyBase.fontWeight.semiBold,
    letterSpacing: typographyBase.letterSpacing.compact,
    lineHeight: typographyBase.lineHeight.lg,
  },

  titleSmall: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.md,
    fontWeight: typographyBase.fontWeight.semiBold,
    letterSpacing: typographyBase.letterSpacing.compact,
    lineHeight: typographyBase.lineHeight.md,
  },

  subTitle: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.md,
    fontWeight: typographyBase.fontWeight.medium,
    letterSpacing: typographyBase.letterSpacing.compact,
    lineHeight: typographyBase.lineHeight.md,
  },

  bodyLarge: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.md,
    fontWeight: typographyBase.fontWeight.medium,
    letterSpacing: typographyBase.letterSpacing.default,
    lineHeight: typographyBase.lineHeight.md,
    paragraphSpacing: spacing.md,
  },

  bodyMedium: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.sm,
    fontWeight: typographyBase.fontWeight.regular,
    letterSpacing: typographyBase.letterSpacing.default,
    lineHeight: typographyBase.lineHeight.md,
    paragraphSpacing: spacing.sm,
  },

  bodySmall: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.xs,
    fontWeight: typographyBase.fontWeight.regular,
    letterSpacing: typographyBase.letterSpacing.default,
    lineHeight: typographyBase.lineHeight.sm,
    paragraphSpacing: spacing.sm,
  },

  selectorText: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.sm,
    fontWeight: typographyBase.fontWeight.medium,
    letterSpacing: typographyBase.letterSpacing.default,
    lineHeight: typographyBase.lineHeight.md,
  },

  fieldTitle: {
    fontFamily: typographyBase.fontFamily.default,
    fontSize: typographyBase.fontSize.xs,
    fontWeight: typographyBase.fontWeight.medium,
    lineHeight: typographyBase.lineHeight.sm,
    textTransform: "uppercase" as const,
  },
} as const;

export const typography = {
  ...typographyBase,
  ...typographicStyles,
} as const;

export type TypographyTokens = typeof typography;
